import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { SIGN_PDF_INTENT_OPTIONS } from "./signPdfSettings";
import "./SignPdfIntentModal.css";

const AllPagesIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <rect
      x="5"
      y="3.5"
      width="11"
      height="14"
      rx="1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M9 5.5h9.5a1.5 1.5 0 0 1 1.5 1.5V19a1.5 1.5 0 0 1-1.5 1.5H10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const OnePageIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <rect
      x="6.5"
      y="3.5"
      width="11"
      height="17"
      rx="1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M9 8h6M9 12h6M9 16h3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PerPageIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <rect
      x="3.5"
      y="4.5"
      width="7"
      height="9"
      rx="1.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.55"
    />
    <rect
      x="13.5"
      y="4.5"
      width="7"
      height="9"
      rx="1.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.55"
    />
    <path
      d="M5.5 16.5h5M15.5 16.5h5M7 19.5h10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.55"
      strokeLinecap="round"
    />
  </svg>
);

const INTENT_ICONS = {
  all: AllPagesIcon,
  current: OnePageIcon,
  per_page: PerPageIcon,
};

/**
 * Post-upload entry: how should signing apply across pages?
 */
const SignPdfIntentModal = ({
  open = false,
  onClose = () => {},
  onSelect = () => {},
}) => {
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const node = (
    <div className="sp-intent-root" role="presentation">
      <button
        type="button"
        className="sp-intent-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="sp-intent-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className="sp-intent-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <h2 id={titleId} className="sp-intent-title">
          How do you want to sign?
        </h2>
        <p className="sp-intent-sub">
          Pick how your signature applies. You can change this later from Signature Fields.
        </p>

        <div className="sp-intent-grid">
          {SIGN_PDF_INTENT_OPTIONS.map((option) => {
            const Icon = INTENT_ICONS[option.id] || AllPagesIcon;
            return (
              <button
                key={option.id}
                type="button"
                className={`sp-intent-card${option.pro ? " is-pro" : ""}`}
                onClick={() => onSelect(option.id)}
              >
                {option.pro ? (
                  <span className="sp-intent-pro">Pro</span>
                ) : null}
                <span className="sp-intent-icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="sp-intent-card__label">{option.label}</span>
                <span className="sp-intent-card__desc">{option.description}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default SignPdfIntentModal;
